import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { faqPageJson } from "@/lib/faq-schema";
import { faqQuestionNodes } from "@/lib/section-faqs";
import { WeightLossOptionsButtons } from "@/components/site/WeightLossOptionsButtons";
import { medicalWebPageScript } from "@/lib/medical-schema";
import { TelehealthCta } from "@/components/site/TelehealthCta";

const TITLE = "Tirzepatide (Zepbound®) in Northern Virginia | Virginia Bariatric";
const DESCRIPTION =
  "Tirzepatide (Zepbound®, Mounjaro®) prescribed by a board-certified bariatric surgeon in Herndon, VA. The most effective weight-loss medication available today.";
const URL = "https://virginiabariatric.com/medical-weight-loss/tirzepatide";

const FAQS = [
  {
    q: "Is Zepbound stronger than Wegovy?",
    a: "In the only head-to-head trial (SURMOUNT-5), tirzepatide (Zepbound) produced greater average weight loss than semaglutide (Wegovy): 20.2% vs. 13.7% of body weight at 72 weeks. Individual responses vary, and factors like insurance coverage, tolerability, and cardiovascular history can make either the better choice for a given patient.",
  },
  {
    q: "How do I get a tirzepatide prescription in Northern Virginia?",
    a: "Start with a medical evaluation at Virginia Bariatric Surgery — in person in Herndon or by telehealth anywhere in Virginia. We review your history, labs, and goals; if tirzepatide is appropriate, we prescribe it, handle prior authorization, and follow you closely through dose escalation.",
  },
  {
    q: "Will I regain weight if I stop Zepbound?",
    a: "In the SURMOUNT-4 trial, patients who stopped tirzepatide regained a substantial portion of lost weight within a year, while those who continued kept losing. Obesity behaves like a chronic condition — which is why we plan maintenance from your first visit, whether that means ongoing medication, a taper strategy, or discussing more durable surgical options. Dr. Fitzer's weight-regain series covers this honestly.",
  },
  {
    q: "Does Zepbound really treat sleep apnea?",
    a: "Yes. Zepbound is FDA-approved for moderate-to-severe obstructive sleep apnea in adults with obesity — the first medication approved for OSA. In trials it markedly reduced apnea events, and some patients were able to reduce or discontinue CPAP under physician guidance. If you snore, wake unrefreshed, or use CPAP, mention it at your visit — it may also strengthen your insurance approval.",
  },
  {
    q: "Is compounded tirzepatide safe?",
    a: "We prescribe only FDA-approved tirzepatide (Zepbound, Mounjaro). With the shortage resolved, mass-compounded versions are no longer permitted, and compounded products were never FDA-reviewed for purity or dosing accuracy. Manufacturer self-pay programs have narrowed the price gap — ask us before considering an unregulated source.",
  },
  {
    q: "Can tirzepatide help with weight regain after bariatric surgery?",
    a: "Yes — GLP-1-class medications are increasingly used for post-surgical weight regain, and a bariatric surgeon is uniquely positioned to manage this: we can evaluate whether the regain is anatomic, behavioral, or metabolic, and treat accordingly. Learn more about our revision surgery and medical-management options.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/tirzepatide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        {
        property: "og:image",
        content:
          "https://virginiabariatric.com/og/medications/og-tirzepatide.png",
      },
        {
        property: "og:image:alt",
        content: "Tirzepatide (Zepbound® & Mounjaro®) for weight loss — Virginia Bariatric Surgery",
      },
        { property: "og:image:width", content: "2400" },
        { property: "og:image:height", content: "1260" },
        { name: "twitter:card", content: "summary_large_image" },
        {
        name: "twitter:image",
        content:
          "https://virginiabariatric.com/og/medications/og-tirzepatide.png",
      },
        {
        name: "twitter:image:alt",
        content: "Tirzepatide (Zepbound® & Mounjaro®) for weight loss — Virginia Bariatric Surgery",
      },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss/tirzepatide") : []),
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/medical-weight-loss/tirzepatide"),
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          // Every visible question on the page — the FAQ block plus the
          // question-style section headings — mapped to FAQPage.
          mainEntity: faqQuestionNodes("/medical-weight-loss/tirzepatide", FAQS),
        }),
      },
    ] : [],
  }),
  component: TirzepatidePage,
});

const linkClass = "text-brand-deep underline hover:no-underline";

function TirzepatidePage() {
  return (
    <article className="mx-auto max-w-3xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-4 pb-10 md:text-[1.125rem] leading-[1.6]">
      <header>
        <h1 className="text-[2.025rem] md:text-[2.7rem] font-serif">
          Tirzepatide (Zepbound<sup>®</sup> &amp; Mounjaro<sup>®</sup>) for Weight Loss in Northern
          Virginia
        </h1>
      </header>

      <p className="mt-3 text-foreground/80">
        Tirzepatide &mdash; sold as <strong>Zepbound<sup>®</sup></strong> for weight loss and{" "}
        <strong>Mounjaro<sup>®</sup></strong> for type 2 diabetes &mdash; is the most effective
        weight-loss medication available today, with average weight loss of about 21% of body weight
        in clinical trials. That approaches the territory once reserved for surgery.
      </p>

      <p className="mt-3 text-foreground/80">
        At Virginia Bariatric Surgery in Herndon, tirzepatide is prescribed and managed by Dr. Matthew
        Fitzer, a fellowship-trained, board-certified bariatric surgeon, as part of a{" "}
        <Link to="/medical-weight-loss" className={linkClass}>
          comprehensive medical weight-loss program
        </Link>{" "}
        &mdash; with in-office and{" "}
        <Link to="/appointments" className={linkClass}>
          telehealth visits
        </Link>{" "}
        for patients across Northern Virginia and the Washington, D.C. metro area, including
        Herndon, Reston, Fairfax, Loudoun, Arlington, and Alexandria.
      </p>

      <TelehealthCta />

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        What is tirzepatide and how does it work?
      </h2>
      <p className="mt-3 text-foreground/80">
        Tirzepatide is the first <strong>dual receptor weight-loss med</strong>. Early-generation
        drugs only worked on the GLP-1 receptor, but Tirzepatide activates both GLP-1 and GIP. It
        therefore stimulates two biological pathways to weight loss instead of one.
      </p>

      <p className="mt-3 text-foreground/80">
        Like GLP-1 medications such as{" "}
        <Link to="/medical-weight-loss/semaglutide" className={linkClass}>
          semaglutide (Wegovy)
        </Link>
        , it curbs appetite, slows stomach emptying, and quiets &ldquo;food noise.&rdquo; The added
        GIP activity appears to amplify weight loss and improve how the body handles fat and sugar.
      </p>

      <p className="mt-3 text-foreground/80">
        We think the GIP effect is why tirzepatide outperforms single-pathway medications in trials.
        It&rsquo;s taken as a once-weekly injection with a pre-filled pen, starting at a low dose and
        stepping up gradually over several months.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Zepbound vs. Mounjaro: what&rsquo;s the difference?
      </h2>
      <ul className="mt-3 space-y-2 text-foreground/80 list-disc pl-5">
        <li>
          <strong>Zepbound<sup>®</sup></strong> is FDA-approved for chronic weight management
          &mdash; and also for moderate-to-severe <strong>obstructive sleep apnea</strong> in adults
          with obesity, the first medication ever approved for OSA.
        </li>
        <li>
          <strong>Mounjaro<sup>®</sup></strong> is the same molecule approved for{" "}
          <Link
            to="/resources/obesity-and-diabetes"
            preload="intent"
            className="text-brand-mid underline"
          >
            type 2 diabetes
          </Link>
          .
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        If you&rsquo;ve been told &ldquo;Mounjaro for weight loss,&rdquo; what you likely need is
        Zepbound &mdash; same active ingredient, but the correct indication matters enormously for
        insurance approval. Our office handles that paperwork daily.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        How much weight will I lose on tirzepatide?
      </h2>
      <p className="mt-3 text-foreground/80">
        The trial data is the strongest ever recorded for a weight-loss medication:
      </p>
      <ul className="mt-3 space-y-2 text-foreground/80 list-disc pl-5">
        <li>
          In SURMOUNT-1, adults on the highest dose lost an{" "}
          <strong>average of about 21% of body weight over 72 weeks</strong>; more than half lost
          20% or more.
        </li>
        <li>
          In SURMOUNT-5, the first head-to-head trial against semaglutide, tirzepatide produced{" "}
          <strong>20.2% average weight loss vs. 13.7%</strong> for Wegovy &mdash; about 50 pounds vs.
          33 pounds for the average participant.
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        For a 250-pound patient, average results equate to roughly 50 pounds. Individual results
        vary with dose, genetics, and lifestyle &mdash; and while tirzepatide narrows the gap,{" "}
        <Link to="/services" className={linkClass}>
          bariatric surgery
        </Link>{" "}
        still produces larger, more durable weight loss for many patients. Because Dr. Fitzer offers
        both, you&rsquo;ll get an honest comparison, not a sales pitch &mdash; see his analysis in{" "}
        <Link
          to="/dr-fitzer-dissects-the-news/$slug"
          params={{ slug: "semaglutide-versus-weight-loss-surgery-outcomes" }}
          className={linkClass}
        >
          Wegovy vs. Surgery Outcomes
        </Link>
        .
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Health benefits beyond the scale
      </h2>
      <ul className="mt-3 space-y-2 text-foreground/80 list-disc pl-5">
        <li>
          <strong>Sleep apnea.</strong> Zepbound is FDA-approved to treat moderate-to-severe
          obstructive sleep apnea in adults with obesity &mdash; in trials it substantially reduced
          breathing interruptions, and many patients reduce or eliminate CPAP dependence.
        </li>
        <li>
          <strong>Blood sugar.</strong> Tirzepatide produces powerful A1c reductions and can prevent
          progression from prediabetes to diabetes.
        </li>
        <li>
          <strong>Metabolic health.</strong> Trials show meaningful improvements in blood pressure,
          triglycerides, cholesterol, and waist circumference.
        </li>
        <li>
          Patients routinely report better energy, less joint pain, and improved mobility as weight
          comes down.
        </li>
      </ul>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Who is a candidate for tirzepatide?
      </h2>
      <p className="mt-3 text-foreground/80">
        Zepbound may be right for you if you have a <strong>BMI of 30 or higher</strong>, or a{" "}
        <strong>BMI of 27 or higher</strong> with a weight-related condition such as high blood
        pressure, sleep apnea, or high cholesterol &mdash; or if you have moderate-to-severe
        obstructive sleep apnea with obesity. Selected patients with a{" "}
        <strong>BMI under 27</strong> are also candidates. It should not be used during pregnancy or
        with a personal or family history of medullary thyroid carcinoma or MEN 2 syndrome, and a
        history of pancreatitis or significant GI disease needs careful evaluation first. Every
        prescription at our practice follows a full medical evaluation &mdash; check{" "}
        <Link to="/medical-weight-loss/qualifications" className={linkClass}>
          Do I qualify for medication?
        </Link>{" "}
        or{" "}
        <Link to="/appointments" className={linkClass}>
          request an appointment
        </Link>
        .
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Side effects and safety: why supervision matters
      </h2>
      <p className="mt-3 text-foreground/80">
        Tirzepatide&rsquo;s side-effect profile is similar to other GLP-1 medications &mdash; mostly
        mild and related to digestion (nausea, constipation, diarrhea), most noticeable during dose
        increases, and generally improving with time. In the head-to-head trial its tolerability was
        comparable to semaglutide&rsquo;s. Less common risks include gallbladder disease and
        pancreatitis. Because weight loss on tirzepatide can be rapid, protecting muscle mass with
        adequate protein and strength activity is a core part of our protocol &mdash; our dietitian
        sets targets and we monitor progress at follow-ups. Dr. Fitzer also tracks emerging safety
        research on GLP-1s closely and writes about it candidly in{" "}
        <Link to="/dr-fitzer-dissects-the-news" className={linkClass}>
          Dr. Fitzer Dissects the News
        </Link>
        .
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Tirzepatide vs. semaglutide: which should you choose?
      </h2>
      <p className="mt-3 text-foreground/80">
        Head-to-head, tirzepatide wins on average weight loss (20.2% vs. 13.7% in SURMOUNT-5). But
        the right choice is individual:
      </p>
      <ul className="mt-3 space-y-2 text-foreground/80 list-disc pl-5">
        <li>
          <strong>Choose tirzepatide</strong> when maximum weight loss is the priority, or when
          sleep apnea is part of the picture.
        </li>
        <li>
          <strong>Semaglutide may fit better</strong> when your insurance covers it preferentially,
          or when established cardiovascular disease makes Wegovy&rsquo;s proven heart-protection
          data (SELECT trial) decisive.
        </li>
        <li>
          <strong>Coverage often decides.</strong> Formularies frequently prefer one over the other;
          we prescribe both and will match the medication to your coverage and your health profile.
        </li>
      </ul>
      <p className="mt-3 text-foreground/80">
        Read more about{" "}
        <Link to="/medical-weight-loss/semaglutide" className={linkClass}>
          semaglutide (Wegovy &amp; Ozempic)
        </Link>{" "}
        or see all{" "}
        <Link to="/medical-weight-loss/medications" className={linkClass}>
          weight-loss medications
        </Link>{" "}
        we prescribe.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        When medication isn&rsquo;t enough
      </h2>
      <p className="mt-3 text-foreground/80">
        Tirzepatide is a powerful tool, but it&rsquo;s not a good fit for everyone. Some patients are
        unresponsive to it and plateau short of their health goals. Others can&rsquo;t tolerate the
        medication because of nausea or other side effects. For many, long-term tirzepatide is just
        too expensive. This is where our practice is different: as a full-service bariatric center,
        we will speak frankly about when tirzepatide isn&rsquo;t likely to help you achieve your
        goals. There are times when opting for a{" "}
        <Link to="/services/sleeve" className={linkClass}>
          gastric sleeve
        </Link>{" "}
        or{" "}
        <Link to="/services/bypass" className={linkClass}>
          gastric bypass
        </Link>{" "}
        is wiser overall than continuing to escalate medication. Obesity is a chronic disease; the
        best treatment is the one you can sustain for the long, long term.
      </p>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Cost and insurance coverage in Virginia
      </h2>
      <p className="mt-3 text-foreground/80">
        Commercial coverage for Zepbound continues to improve, especially with the sleep-apnea
        indication &mdash; prior authorization is usually required, and our office manages it. As of
        2026, a Medicare pilot program covers GLP-1s for weight management for eligible
        beneficiaries, and manufacturer self-pay options (including single-dose vials at reduced
        prices) have made out-of-pocket treatment far more affordable than list price. We also
        prescribe{" "}
        <Link to="/medical-weight-loss/oral-medications" className={linkClass}>
          affordable non-GLP-1 alternatives
        </Link>{" "}
        for patients who need a lower-cost option or cannot use a GLP-1. Start with our{" "}
        <Link to="/medical-weight-loss/insurance" className={linkClass}>
          medication insurance coverage guide
        </Link>{" "}
        &mdash; we&rsquo;ll help you find the lowest-cost path to treatment.
      </p>


      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">Tirzepatide FAQs</h2>
      <div className="mt-3 space-y-4">
        {FAQS.map((f) => (
          <div key={f.q}>
            <h3 className="font-serif text-brand-mid text-[1.125rem]">{f.q}</h3>
            <p className="mt-1 text-foreground/80">{f.a}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-6 text-[1.35rem] md:text-[1.6875rem] font-serif">
        Next steps: start tirzepatide with the region&rsquo;s weight-loss experts
      </h2>
      <p className="mt-3 text-foreground/80">
        One visit gets you a clear answer on candidacy, coverage, and what results you can
        realistically expect.{" "}
        <Link to="/appointments" className={linkClass}>
          Request an appointment
        </Link>{" "}
        or call{" "}
        <a
          href="tel:+17037099771"
          aria-label="Call Virginia Bariatric Surgery at 703-709-9771"
          className={linkClass}
        >
          (703) 709-9771
        </a>
        . Serving Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, and the greater
        Washington, D.C. metro area &mdash; telehealth available throughout Virginia.
      </p>

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/appointments"
          className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Request a consultation
        </Link>
        <WeightLossOptionsButtons />
      </div>
    </article>
  );
}
